import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select } from "@/components/UI";
import { ClockCounterClockwise, ArrowsClockwise, FunnelSimple, User, Info } from "@phosphor-icons/react";

const ROLE_COLORS = {
  owner: "bg-[#2D4A36] text-white",
  manager: "bg-[#417251] text-white",
  vet: "bg-[#7c6f3f] text-white",
  employee: "bg-[#8a938c] text-white",
};

const ACTION_TONES = {
  LOGIN_OK: "bg-[#e6f0ea] text-[#2D4A36]",
  LOGIN_FAIL: "bg-[#fde1da] text-[#8c2f1c]",
  LOGOUT: "bg-[#eeece5] text-[#5e675e]",
  EMAIL_VERIFIED: "bg-[#e6f0ea] text-[#2D4A36]",
  DEVICE_APPROVED: "bg-[#e6f0ea] text-[#2D4A36]",
  DEVICE_REJECTED: "bg-[#fde1da] text-[#8c2f1c]",
  PASSWORD_RESET_REQUEST: "bg-[#fbf1d9] text-[#7c6f3f]",
  USER_CREATE: "bg-[#e6f0ea] text-[#2D4A36]",
  USER_UPDATE: "bg-[#fbf1d9] text-[#7c6f3f]",
  USER_DELETE: "bg-[#fde1da] text-[#8c2f1c]",
  FINANCE_CREATE: "bg-[#e6f0ea] text-[#2D4A36]",
  FINANCE_DELETE: "bg-[#fde1da] text-[#8c2f1c]",
  MILK_UPSERT: "bg-[#e6f0ea] text-[#2D4A36]",
  MILK_DELETE: "bg-[#fde1da] text-[#8c2f1c]",
  STOCK_CREATE: "bg-[#e6f0ea] text-[#2D4A36]",
  STOCK_UPDATE: "bg-[#fbf1d9] text-[#7c6f3f]",
  STOCK_PRICE_UPDATE: "bg-[#fbf1d9] text-[#7c6f3f]",
  STOCK_DELETE: "bg-[#fde1da] text-[#8c2f1c]",
  MEDICINE_CREATE: "bg-[#e6f0ea] text-[#2D4A36]",
  MEDICINE_UPDATE: "bg-[#fbf1d9] text-[#7c6f3f]",
  MEDICINE_DELETE: "bg-[#fde1da] text-[#8c2f1c]",
  INSEMINATION_CREATE: "bg-[#e6f0ea] text-[#2D4A36]",
  PREGNANCY_CHECK_CREATE: "bg-[#e6f0ea] text-[#2D4A36]",
  TREATMENT_CREATE: "bg-[#e6f0ea] text-[#2D4A36]",
  BACKUP_DOWNLOAD: "bg-[#eeece5] text-[#5e675e]",
  RESTORE_APPLY: "bg-[#fbf1d9] text-[#7c6f3f]",
  SYSTEM_RESET: "bg-[#fde1da] text-[#8c2f1c]",
  IMPORT_APPLY: "bg-[#eeece5] text-[#5e675e]",
};

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (_) {
    return iso.slice(0, 19).replace("T", " ");
  }
}

function fmtChanges(changes) {
  if (!changes || typeof changes !== "object") return null;
  const pairs = Object.entries(changes).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!pairs.length) return null;
  return pairs.map(([k, v]) => {
    let val = v;
    if (typeof v === "object") { try { val = JSON.stringify(v); } catch { val = String(v); } }
    if (typeof val === "string" && val.length > 60) val = val.slice(0, 57) + "…";
    return `${k}: ${val}`;
  }).join(" · ");
}

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ actions: [], user_emails: [], entity_types: [], total_records: 0 });

  const [filters, setFilters] = useState({
    action: "",
    user_email: "",
    entity_type: "",
    start: "",
    end: "",
    limit: 500,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) params[k] = v; });
      const [logs, sum] = await Promise.all([
        api.get("/audit-logs", { params }),
        api.get("/audit-logs/summary"),
      ]);
      setItems(logs.data.items || []);
      setTotal(logs.data.total_matching || 0);
      setSummary(sum.data);
    } catch (e) {
      toast.error(e.message || "Denetim kayıtları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; filtreler "Uygula"/"Yenile" ile elle yüklenir
  }, []);

  const activeFilterCount = useMemo(() =>
    ["action", "user_email", "entity_type", "start", "end"].filter(k => filters[k]).length
  , [filters]);

  const resetFilters = () => {
    setFilters({ action: "", user_email: "", entity_type: "", start: "", end: "", limit: 500 });
  };

  return (
    <div data-testid="audit-log-page">
      <PageHeader
        title="Denetim İzi"
        subtitle="Sistemde yapılan tüm önemli işlemlerin kalıcı kaydı — kim, ne zaman, neyi değiştirdi."
        actions={
          <Button variant="secondary" onClick={load} data-testid="audit-refresh-btn">
            <ArrowsClockwise size={16} weight="bold" className="inline mr-1.5" />
            Yenile
          </Button>
        }
      />

      {/* Özet kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatBlock label="Toplam Kayıt" value={summary.total_records || 0} testId="audit-stat-total" />
        <StatBlock label="Farklı Kullanıcı" value={summary.user_emails?.length || 0} testId="audit-stat-users" />
        <StatBlock label="Farklı Eylem" value={summary.actions?.length || 0} testId="audit-stat-actions" />
        <StatBlock label="Gösterilen" value={items.length} hint={`toplam ${total} eşleşti`} testId="audit-stat-shown" />
      </div>

      {/* Filtreler */}
      <Card className="mb-4" testId="audit-filters-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#2D4A36]">
            <FunnelSimple size={18} weight="duotone" /> Filtreler {activeFilterCount > 0 && <span className="text-xs bg-[#2D4A36] text-white px-2 py-0.5 rounded-full">{activeFilterCount}</span>}
          </div>
          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="text-xs text-[#8a938c] hover:text-[#2D4A36]" data-testid="audit-reset-filters">
              Filtreleri temizle
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <FilterField label="Eylem">
            <Select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-filter-action">
              <option value="">Tümü</option>
              {summary.actions?.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Kullanıcı">
            <Select value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })} data-testid="audit-filter-user">
              <option value="">Tümü</option>
              {summary.user_emails?.map((e) => <option key={e} value={e}>{e}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Modül">
            <Select value={filters.entity_type} onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })} data-testid="audit-filter-entity">
              <option value="">Tümü</option>
              {summary.entity_types?.map((e) => <option key={e} value={e}>{e}</option>)}
            </Select>
          </FilterField>
          <FilterField label="Başlangıç">
            <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="audit-filter-start" />
          </FilterField>
          <FilterField label="Bitiş">
            <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value ? `${e.target.value}T23:59:59` : "" })} data-testid="audit-filter-end" />
          </FilterField>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={load} data-testid="audit-apply-filters">Uygula</Button>
        </div>
      </Card>

      {/* Tablo */}
      <Card testId="audit-list-card">
        {loading ? (
          <div className="text-center py-10 text-[#8a938c]">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-[#8a938c]">
            <Info size={32} weight="duotone" className="inline mb-2 opacity-50" />
            <div>Kayıt bulunamadı. Filtreleri değiştirip tekrar deneyin.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#8a938c]">
                  <th className="py-2 pr-3">Zaman</th>
                  <th className="py-2 pr-3">Kullanıcı</th>
                  <th className="py-2 pr-3">Rol</th>
                  <th className="py-2 pr-3">Eylem</th>
                  <th className="py-2 pr-3">Modül</th>
                  <th className="py-2 pr-3">Detay</th>
                  <th className="py-2 pr-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const changesStr = fmtChanges(item.changes);
                  return (
                    <tr key={item.id || idx} className="border-b border-[#F0EEE8] hover:bg-[#FAF9F5]" data-testid={`audit-row-${idx}`}>
                      <td className="py-2.5 pr-3 whitespace-nowrap font-mono text-xs text-[#5e675e]">
                        <ClockCounterClockwise size={12} className="inline mr-1 opacity-60" />
                        {fmtDateTime(item.timestamp)}
                      </td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-1">
                          <User size={14} weight="duotone" className="text-[#8a938c]" />
                          <span className="font-medium">{item.user_name || item.user_email || "—"}</span>
                        </div>
                        {item.user_name && item.user_email && (
                          <div className="text-[11px] text-[#8a938c]">{item.user_email}</div>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        {item.user_role ? (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${ROLE_COLORS[item.user_role] || "bg-[#8a938c] text-white"}`}>
                            {item.user_role}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className={`text-xs px-2 py-1 rounded-md font-medium ${ACTION_TONES[item.action] || "bg-[#eeece5] text-[#5e675e]"}`}>
                          {item.action_label || item.action}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-[#5e675e]">{item.entity_type || "—"}</td>
                      <td className="py-2.5 pr-3 text-[#3f463f] max-w-[380px]">
                        <div className="truncate" title={changesStr || item.detail || ""}>
                          {item.detail && <span className="font-medium">{item.detail}</span>}
                          {item.detail && changesStr && <span className="text-[#8a938c]"> · </span>}
                          {changesStr && <span className="text-xs text-[#5e675e]">{changesStr}</span>}
                          {!item.detail && !changesStr && "—"}
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 font-mono text-xs text-[#8a938c]">{item.ip || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {items.length > 0 && total > items.length && (
          <div className="mt-4 text-xs text-[#8a938c] text-center">
            İlk {items.length} kayıt gösteriliyor. Toplam {total} kayıt eşleşiyor — filtrelerle daralttığınızda tümünü görebilirsiniz.
          </div>
        )}
      </Card>
    </div>
  );
}

function StatBlock({ label, value, hint, testId }) {
  return (
    <div className="border border-[#E5E2DC] rounded-md p-4 bg-white" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-[#8a938c]">{label}</div>
      <div className="text-2xl md:text-3xl font-semibold mt-1 text-[#1A1D1A]">{value}</div>
      {hint && <div className="text-[11px] text-[#8a938c] mt-1">{hint}</div>}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#8a938c] mb-1">{label}</div>
      {children}
    </div>
  );
}
